(function (root) {
  'use strict';
  const active = group => group.kind === 'included' || group.value !== '0';
  const keyFor = (prefix, groups) => prefix + groups.filter(g => g.kind === 'choice').sort((a, b) => a.key.localeCompare(b.key, 'en')).map(g => `|${g.key}=${g.value}`).join('');
  function validate(groups, slots) {
    if (groups.some(g => g.required && g.value === '0')) return 'missing_choice';
    const keys = groups.map(g => g.key);
    if (keys.some(k => !/^[a-z0-9_-]+$/.test(k)) || new Set(keys).size !== keys.length) return 'invalid_setup';
    const positioned = groups.filter(g => active(g) && g.position === 'main');
    if (positioned.length > slots) return 'too_many';
    const occupied = positioned.filter(g => g.slot);
    if (occupied.some(g => !Number.isInteger(Number(g.slot)) || Number(g.slot) < 1 || Number(g.slot) > slots)) return 'invalid_setup';
    if (new Set(occupied.map(g => g.slot)).size !== occupied.length) return 'duplicate';
    if (occupied.length !== positioned.length) return 'missing_positions';
    return '';
  }
  const core = { active, keyFor, validate };
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (!root.customElements || root.customElements.get('mp-configurator')) return;
  class MPConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('form');
      this.buttons = Array.from(this.querySelectorAll('[type=submit]'));
      this.text = JSON.parse(this.querySelector('[data-config-text]').textContent);
      this.variants = JSON.parse(this.querySelector('[data-config-variants]').textContent);
      this.index = JSON.parse(this.querySelector('[data-config-index]')?.textContent || 'null');
      this.rules = JSON.parse(this.querySelector('[data-config-rules]')?.textContent || '[]') || [];
      this.loading = new Map();
      this.failed = new Set();
      if (this.index) {
        const loaded = new Map(this.variants.map(v => [String(v.id), v]));
        this.variants = this.index.variants.map(v => ({ ...v, ...loaded.get(String(v.id)) }));
      }
      this.variantMap = new Map(this.variants.map(v => [v.key, v]));
      this.slots = Number(this.dataset.slots);
      this.rate = Number(this.dataset.vat) / 100;
      this.base = Number(this.dataset.baseHt);
      this.currency = this.dataset.currency || 'EUR';
      this.money = n => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: this.currency }).format(n / 100);
      this.form.addEventListener('change', () => { this.failed.clear(); this.update(); });
      this.form.addEventListener('submit', event => this.submit(event));
      this.querySelector('[data-reset]').addEventListener('click', () => { this.form.reset(); this.update(); });
      if (this.dataset.native === 'true') {
        const groups = this.groups();
        const baseGroups = groups.map(g => ({ ...g, value: g.el.querySelector('input[type=radio][checked]')?.value || '0' }));
        const baseVariant = this.variants.find(v => String(v.id) === this.dataset.baseVariantId);
        this.querySelectorAll('input[data-price-shopify]').forEach(radio => {
          if (radio.dataset.priceShopify !== '') return;
          const group = radio.closest('[data-kind]');
          const candidate = baseGroups.map(g => g.key === group.dataset.key ? { ...g, value: radio.value } : g);
          const reference = this.variantMap.get(keyFor(this.dataset.prefix, candidate));
          if (Number.isFinite(reference?.price) && Number.isFinite(baseVariant?.price)) radio.dataset.priceShopify = String(reference.price - baseVariant.price);
        });
      }
      this.renderEquipment();
      this.update();
    }
    renderEquipment() {
      const host = document.querySelector("[data-mp-equipment]");
      const original = this.querySelector(".mp-config__included");
      if (!host || !original) return;
      const details = this.node("details", undefined, "mp-config__included mp-config__included--top");
      const summary = this.node("summary");
      summary.append(this.node("strong", this.text.equipment_open), this.node("small", this.text.equipment_hint), this.node("span", "+", "mp-config__expand"));
      details.append(summary);
      const list = this.node("div", undefined, "mp-config__included-list");
      this.groups().filter(g => g.kind === "included" || g.required).forEach(g => {
        const row = this.node("div", undefined, "mp-config__included-row");
        row.append(this.node("span", "✓", "mp-config__included-tick"));
        const baseline = g.required ? g.el.querySelector('input[type=radio][checked]') : null;
        const card = baseline?.closest('label') || g.el;
        const image = card.querySelector("img");
        if (image) { const copy = image.cloneNode(); copy.className = "mp-config__included-image"; row.append(copy); }
        const content = this.node("div");
        content.append(this.node("strong", baseline?.dataset.label || g.title));
        const description = card.querySelector(".mp-config__option-description") || g.el.querySelector(".mp-config__description, p");
        if (description) content.append(this.node("p", description.textContent));
        row.append(content); list.append(row);
      });
      details.append(list); host.replaceChildren(details); original.hidden = true;
    }
    groups() {
      return Array.from(this.querySelectorAll('[data-kind]')).map(el => {
        const selected = el.querySelector('input[type=radio]:checked');
        return { el, key: el.dataset.key, kind: el.dataset.kind, title: el.dataset.title, position: el.dataset.position, required: el.dataset.required === 'true', value: selected?.value || '0', label: selected?.dataset.label || el.dataset.title, price: selected?.dataset.priceShopify !== undefined ? (selected.dataset.priceShopify === '' ? '' : Number(selected.dataset.priceShopify) / (this.dataset.taxIncluded === 'true' ? 100 * (1 + this.rate) : 100)) : selected?.dataset.price ?? '0', code: selected?.dataset.code || el.querySelector('[data-supplier-code]')?.dataset.supplierCode || '', slot: el.querySelector('[data-slot]')?.value || '' };
      });
    }
    applyRules() {
      const groups = this.groups();
      this.blocked = new Set();
      this.querySelectorAll('[data-compatibility-note]').forEach(el => { el.hidden = true; el.textContent = ''; });
      for (const rule of this.rules) {
        if (!Object.entries(rule.when).every(([key, value]) => groups.some(g => g.key === key && g.value === value))) continue;
        for (const [key, values] of Object.entries(rule.disable)) {
          const group = groups.find(g => g.key === key);
          if (!group) continue;
          for (const value of values) {
            this.blocked.add(key + '=' + value);
            const selected = group.el.querySelector('input[type=radio]:checked');
            if (selected?.value === value) {
              const fallback = Array.from(group.el.querySelectorAll('input[type=radio]')).find(el => !values.includes(el.value));
              if (fallback) fallback.checked = true;
            }
          }
          const note = group.el.querySelector('[data-compatibility-note]');
          if (note) { note.textContent = rule.message; note.hidden = false; }
        }
      }
    }
    async loadVariant(entry) {
      const identity = String(entry.id);
      if (this.loading.has(identity) || this.failed.has(identity)) return;
      const request = (async () => {
        const url = new URL(this.dataset.productUrl, window.location.origin);
        url.searchParams.set('variant', identity);
        url.searchParams.set('section_id', 'mp-config-variant-data');
        const response = await fetch(url, { headers: { Accept: 'text/html' }, cache: 'no-store' });
        if (!response.ok) throw new Error('variant');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const data = JSON.parse(html.querySelector('[data-mp-variant]')?.textContent || 'null');
        if (!data || String(data.id) !== identity || data.key !== entry.key || !Number.isInteger(data.price) || typeof data.available !== 'boolean') throw new Error('variant');
        Object.assign(entry, data);
      })();
      this.loading.set(identity, request);
      try { await request; } catch (error) { this.failed.add(identity); }
      finally {
        this.loading.delete(identity);
        if (this.isConnected && keyFor(this.dataset.prefix, this.groups()) === entry.key) this.update();
      }
    }
    node(tag, text, className) {
      const el = document.createElement(tag);
      if (text !== undefined) el.textContent = text;
      if (className) el.className = className;
      return el;
    }
    update() {
      if (this.busy) return false;
      this.applyRules();
      let groups = this.groups();
      groups.forEach(g => {
        const select = g.el.querySelector('[data-slot]');
        if (!select) return;
        select.closest('label').hidden = !active(g);
        select.disabled = !active(g);
        select.required = active(g);
        if (!active(g)) select.value = '';
      });
      groups = this.groups();
      groups.forEach(g => {
        const select = g.el.querySelector('[data-slot]');
        if (select) Array.from(select.options).forEach(o => { o.disabled = !!o.value && groups.some(other => other.key !== g.key && active(other) && other.position === 'main' && other.slot === o.value); });
        const full = g.position === "main" && g.kind === "choice" && !active(g) && groups.filter(other => active(other) && other.position === "main").length >= this.slots;
        const note = g.el.querySelector("[data-capacity-note]");
        if (note) { note.hidden = !full; note.textContent = full ? this.text.capacity_full + " " + groups.filter(other => active(other) && other.kind === "choice" && other.position === "main").map(other => other.title).join(" / ") : ""; }
        g.el.querySelectorAll('input[type=radio]').forEach(radio => {
          const nativePrice = radio.dataset.priceShopify;
          const price = nativePrice !== undefined ? (nativePrice === '' ? '' : Number(nativePrice) / (this.dataset.taxIncluded === 'true' ? 100 * (1 + this.rate) : 100)) : radio.dataset.price;
          const label = radio.closest('label').querySelector('[data-option-price]');
          if (label) label.textContent = price !== '' && Number.isFinite(Number(price)) ? `+ ${this.money(Math.round(Number(price) * 100 * (1 + this.rate)))} ${this.text.ttc}` : this.text.price_pending;
          radio.disabled = this.blocked.has(g.key + '=' + radio.value) || !this.validSetup(groups) || (g.position === 'main' && radio.value !== '0' && !active(g) && groups.filter(other => active(other) && other.position === 'main').length >= this.slots);
        });
      });
      const configuration = keyFor(this.dataset.prefix, groups);
      const mapped = this.variantMap.get(configuration);
      const variant = Number.isFinite(mapped?.price) ? mapped : null;
      if (mapped && !variant && this.index) this.loadVariant(mapped);
      const selected = groups.filter(active);
      const pending = selected.some(g => g.kind === 'choice' && (g.price === '' || !Number.isFinite(Number(g.price))));
      const estimateHt = Math.round(this.base * 100) + selected.reduce((sum, g) => sum + (g.kind === 'choice' && g.price !== '' ? Math.round(Number(g.price) * 100) : 0), 0);
      let ht = estimateHt, ttc = Math.round(ht * (1 + this.rate));
      if (variant) {
        if (this.dataset.taxIncluded === 'true') { ttc = variant.price; ht = Math.round(ttc / (1 + this.rate)); }
        else { ht = variant.price; ttc = Math.round(ht * (1 + this.rate)); }
      }
      const quantity = Number(this.form.elements.quantity.value);
      const q = Number.isInteger(quantity) && quantity >= 1 && quantity <= 99 ? quantity : 1;
      this.querySelectorAll('[data-total-ttc]').forEach(el => el.textContent = `${this.money(ttc * q)} ${this.text.ttc}`);
      this.querySelector('[data-total-ht]').textContent = `${this.money(ht * q)} ${this.text.ht}`;
      this.querySelector('[data-estimate]').textContent = mapped && !variant ? this.text.loading : (!variant ? (pending ? this.text.price_pending : this.text.estimate) : '');
      const summary = this.querySelector('[data-summary]'); summary.replaceChildren();
      groups.filter(g => g.kind === 'choice').forEach(g => {
        const li = this.node('li'); li.append(this.node('span', g.title), this.node('strong', g.label)); summary.append(li);
      });
      const slotSummary = this.querySelector('[data-slot-summary]'); slotSummary.replaceChildren();
      for (let i = 1; i <= this.slots; i++) {
        const item = groups.find(g => active(g) && g.position === 'main' && g.slot === String(i));
        const tile = this.node('div', undefined, 'mp-config__slot');
        tile.append(this.node('b', String(i)), this.node('span', item?.title || this.text.free)); slotSummary.append(tile);
      }
      const properties = this.querySelector('[data-properties]'); properties.replaceChildren();
      const property = (name, value) => { const input = document.createElement('input'); input.type = 'hidden'; input.name = `properties[${name}]`; input.value = value; properties.append(input); };
      property('_' + this.text.configuration_property, configuration);
      groups.filter(g => g.kind === 'included').forEach(g => property(`${this.text.included} — ${g.title}`, this.text.included + (g.code ? ` (${g.code})` : '')));
      groups.filter(g => g.kind === 'choice').forEach(g => property(g.title, g.label + (active(g) && g.code ? ` (${g.code})` : '')));
      groups.filter(g => active(g) && g.position === 'main').forEach(g => property(`${this.text.position_property} — ${g.title}`, g.slot));
      groups.filter(g => active(g) && g.position === 'fixed').forEach(g => property(`${this.text.position_property} — ${g.title}`, this.querySelector('.mp-config__fixed')?.textContent.trim() || ''));
      let error = validate(groups, this.slots);
      if (this.dataset.native === 'true' && (!this.dataset.baseVariantId || !this.variants.some(v => String(v.id) === this.dataset.baseVariantId) || this.variants.length !== this.variantMap.size)) error = 'invalid_setup';
      else if (!this.validSetup(groups) || this.dataset.enabled !== 'true' || this.currency !== 'EUR' || (!this.index && this.variants.length >= 250) || (this.index && (this.index.count !== this.variants.length || new Set(this.variants.map(v => String(v.id))).size !== this.variants.length))) error = 'invalid_setup';
      else if (!error && mapped && !variant) error = this.failed.has(String(mapped.id)) ? 'load_error' : 'loading';
      else if (!error && pending && !variant) error = 'pricing_pending';
      else if (!error && !variant) error = 'unmapped';
      else if (!error && !variant.available) error = 'unavailable';
      this.querySelectorAll('[data-status]').forEach(el => { el.textContent = error ? this.text[error] : ''; el.classList.remove('mp-config__status--success'); });
      this.buttons.forEach(button => button.disabled = !!error);
      const id = this.form.querySelector('[name=id]'); id.value = variant?.id || ''; id.disabled = !!error;
      this.currentVariant = variant;
      return !error;
    }
    validSetup(groups) {
      return Number.isFinite(this.base) && this.base >= 0 && Number.isInteger(this.slots) && this.slots >= 0 && /^[A-Za-z0-9_-]+$/.test(this.dataset.prefix) && groups.length > 0 && groups.filter(g => g.kind === 'choice').length <= 10;
    }
    async refreshCart(result, sections, drawer) {
      sections.forEach(section => {
        const html = result.sections?.[section.id];
        if (!html) return;
        const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector || '.shopify-section');
        if (target && source) target.innerHTML = source.innerHTML;
      });
      if (drawer) {
        drawer.classList.remove('is-empty');
        drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
      }
      const response = await fetch(this.dataset.cartUrl + '.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      if (!result.sections?.['cart-icon-bubble']) {
        const icon = document.getElementById('cart-icon-bubble');
        if (icon) {
          let bubble = icon.querySelector('.cart-count-bubble');
          if (!bubble) { bubble = this.node('div', undefined, 'cart-count-bubble'); icon.append(bubble); }
          bubble.textContent = String(cart.item_count);
        }
      }
      document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart, sectionId: this.id } }));
    }
    async submit(event) {
      event.preventDefault();
      if (this.busy || !this.update() || !this.form.reportValidity()) return;
      this.busy = true; this.buttons.forEach(button => { button.disabled = true; button.textContent = this.text.adding; });
      this.querySelectorAll('input[type=radio], [data-slot], [data-reset], [name=quantity]').forEach(el => el.disabled = true);
      try {
        const data = new FormData(this.form);
        data.set('quantity', this.form.elements.quantity.value);
        const drawer = document.querySelector('cart-drawer');
        const sections = drawer?.getSectionsToRender?.() || [{ id: 'cart-icon-bubble' }];
        data.set('sections', sections.map(section => section.id).slice(0, 5).join(','));
        data.set('sections_url', window.location.pathname);
        const response = await fetch(this.dataset.addUrl || this.form.action.replace(/\/cart\/add$/, '/cart/add.js'), { method: 'POST', headers: { Accept: 'application/json' }, body: data });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || this.text.error);
        await this.refreshCart(result, sections, drawer).catch(() => {});
        this.busy = false;
        this.querySelectorAll('input[type=radio], [data-slot], [data-reset], [name=quantity]').forEach(el => el.disabled = false);
        this.buttons.forEach(button => button.textContent = this.text.add);
        this.update();
        this.querySelectorAll('[data-status]').forEach(el => { el.textContent = this.text.added; el.classList.add('mp-config__status--success'); });
        this.querySelectorAll('[data-view-cart]').forEach(el => el.hidden = false);
      } catch (error) {
        this.busy = false;
        this.querySelectorAll('input[type=radio], [data-slot], [data-reset], [name=quantity]').forEach(el => el.disabled = false);
        this.buttons.forEach(button => button.textContent = this.text.add); this.update();
        this.querySelectorAll('[data-status]').forEach(el => el.textContent = error.message || this.text.error);
      }
    }
  }
  root.customElements.define('mp-configurator', MPConfigurator);
})(typeof window !== 'undefined' ? window : globalThis);


